// Ch83 · "are replies landing?", on the surface people actually look at.
//
// The metric is admin_truth.messages.inbound_sms; the reading is
// lib/inboundReplies.readInbound. This file only draws it — which is why the
// interesting tests live next to the reading, not next to the markup.
//
// It is a CARD rather than a tile in AdminTruthBand because the payload is not
// a scalar: the number that separates "quiet" from "broken" is the AGE of the
// most recent reply, and a tile that showed only a count would hide exactly the
// figure the metric was built to surface.

import React from "react";
import { Box, Paper, Stack, Typography, alpha, useTheme } from "@mui/material";
import { readInbound, type InboundReplies } from "../../lib/inboundReplies";
import type { AdminTruthEnvelope } from "../../services/adminTruthService";

export interface InboundRepliesCardProps {
  /** The `messages.inbound_sms` envelope, or undefined when absent from the read. */
  envelope?: AdminTruthEnvelope | { status: string; value: unknown; note?: string };
}

export default function InboundRepliesCard({ envelope }: InboundRepliesCardProps) {
  const theme = useTheme();
  const ok = envelope && envelope.status === "ok" && envelope.value && typeof envelope.value === "object";
  const reading = readInbound(ok ? (envelope!.value as unknown as InboundReplies) : null);

  const toneColor =
    reading.tone === "ok"
      ? theme.palette.success.main
      : reading.tone === "bad"
        ? theme.palette.error.main
        : reading.tone === "warn"
          ? theme.palette.warning.main
          : theme.palette.text.disabled;

  return (
    <Paper
      variant="outlined"
      data-testid="inbound-replies-card"
      sx={{ p: 2, borderRadius: 2, borderLeft: `3px solid ${toneColor}`, bgcolor: alpha(toneColor, 0.04) }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography
          variant="caption"
          sx={{ letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 700 }}
          color="text.secondary"
        >
          Are replies landing
        </Typography>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: toneColor }} />
      </Stack>
      <Typography variant="subtitle1" fontWeight={800} data-testid="inbound-headline">
        {reading.headline}
      </Typography>
      <Typography variant="caption" color="text.secondary" data-testid="inbound-detail">
        {reading.detail}
      </Typography>
      {envelope?.note && (
        <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5 }}>
          {envelope.note}
        </Typography>
      )}
    </Paper>
  );
}
